import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Card, Badge, Spinner } from 'react-bootstrap';
import { formatDateTime } from '../../services/profile.service';
import './account-settings-mobile.css';

const AccountSettings = ({ accountDetails, onPayNow, onChangePlan, onViewBillingHistory, loading }) => {
  // State for UI display
  const [subscriptionPlanName, setSubscriptionPlanName] = useState('');
  const [nextPaymentDate, setNextPaymentDate] = useState('');
  const [accountStatus, setAccountStatus] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [showPayNowButton, setShowPayNowButton] = useState(false);
  const [isTrialUser, setIsTrialUser] = useState(false);
  const [showChangePlanButton, setShowChangePlanButton] = useState(true);

  // Initialize UI when account details change
  useEffect(() => {
    if (accountDetails && accountDetails.user && accountDetails.payment) {
      updateUIWithAccountDetails(accountDetails);
    }
  }, [accountDetails]);

  // Update UI with account details
  const updateUIWithAccountDetails = (details) => {
    // Set subscription plan name
    getPlanNameById(details.payment.SubscriptionType)
      .then(name => setSubscriptionPlanName(name || 'N/A'));

    // Set account status
    setAccountStatus(details.user.AccountStatus || 'Unknown');

    // Set billing cycle
    setBillingCycle(details.payment.BillingFrequency || 'Yearly');

    // Format and set next payment date
    const nextPayDate = details.payment.NextPayDate
      ? new Date(details.payment.NextPayDate)
      : null;

    setNextPaymentDate(nextPayDate ? nextPayDate.toLocaleDateString() : 'N/A');

    // Determine if pay now button should be shown
    const shouldShowPayNow = checkPayNowButtonVisibility(details.payment.NextPayDate);
    setShowPayNowButton(shouldShowPayNow);

    // Check if user is on trial plan
    setIsTrialUser(details.payment.SubscriptionType === '3');

    const isPlus = details.payment.SubscriptionType === '2';
    if (isPlus && nextPayDate) {
      const today = new Date();
      const twoMonthsFromNow = new Date(today);
      twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

      // If payment date is more than 2 months away, hide Change Plan button
      setShowChangePlanButton(nextPayDate <= twoMonthsFromNow);
    } else {
      setShowChangePlanButton(true);
    }
  };

  // Get plan name by ID (mocked implementation - replace with API call if needed)
  const getPlanNameById = async (planId) => {
    const planNames = {
      '1': 'Basic',
      '2': 'Plus',
      '3': 'Trial'
    };

    return planNames[planId] || 'Unknown Plan';
  };

  // Check if pay now button should be displayed
  const checkPayNowButtonVisibility = (nextPaymentDate) => {
    if (!nextPaymentDate) return false;

    const today = new Date();
    const paymentDate = new Date(nextPaymentDate);
    const oneMonthBefore = new Date(paymentDate);
    oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

    // Show pay now button if we're within one month of payment date or past it
    return today >= oneMonthBefore;
  };

  // Get status badge variant based on account status
  const getStatusBadgeVariant = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'frozen':
      case 'suspended':
        return 'danger';
      case 'cancelling':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Get date badge variant based on if date is in the past
  const getDateBadgeVariant = (dateStr) => {
    if (dateStr === 'N/A') return 'secondary';

    const date = new Date(dateStr);
    const today = new Date();

    // Remove time portion for comparison
    const dateWithoutTime = new Date(date.setHours(0, 0, 0, 0));
    const todayWithoutTime = new Date(today.setHours(0, 0, 0, 0));

    return dateWithoutTime < todayWithoutTime ? 'danger' : 'success';
  };

  return (
    <>
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" role="status" variant="success" size="md" />
          <p className="mt-2">Loading account data...</p>
        </div>
      ) : (
        <div id="account-mode" className="container-fluid">
  {/* Subscription Details Card */}
  <Card className="mb-4 shadow-sm">
    <Card.Header className="bg-profile text-black d-flex flex-column">
      <h5 className="mb-0">Subscription Details</h5>
      <hr className="mt-2 mb-0 border-top border-gray-300 w-100" />
    </Card.Header>
    <Card.Body className="pt-4 pb-3">
<div className="subscription-grid"> {/* Changed to a custom grid container */}
  {/* Account Status */}
  <div className="subscription-detail-card p-3 rounded bg-light h-100">
    <div className="text-black small mb-1">Account Status</div>
    <span className={`badge w-100 py-2 text-white bg-${getStatusBadgeVariant(accountStatus)} text-center`}>
      {accountStatus}
    </span>
  </div>
  
  {/* Next Payment Date */}
  <div className="subscription-detail-card p-3 rounded bg-light h-100">
    <div className="text-black small mb-1">Next Payment Date</div>
    <span className={`badge w-100 py-2 text-white bg-${getDateBadgeVariant(nextPaymentDate)} text-center`}>
      {nextPaymentDate}
    </span>
  </div>

  {/* Subscription Plan */}
  <div className="subscription-detail-card p-3 rounded bg-light h-100">
    <div className="text-black small mb-1">Subscription Plan</div>
    <span className="badge w-100 py-2 text-white bg-info text-center">
      {subscriptionPlanName}
    </span>
  </div>

  {/* Billing Cycle */}
  <div className="subscription-detail-card p-3 rounded bg-light h-100">
    <div className="text-black small mb-1">Billing Cycle</div>
    <span className="badge w-100 py-2 text-white bg-info text-center">
      {billingCycle}
    </span>
  </div>
</div>
    </Card.Body>
  </Card>

  {/* Billing Actions Card */}
  <Card className="mb-4 shadow-sm">
    <Card.Header className="bg-profile text-black d-flex flex-column">
      <h5 className="mb-0">Billing Actions</h5>
      <hr className="mt-2 mb-0 border-top border-gray-300 w-100" />
    </Card.Header>
    <Card.Body className="py-4">
      <div className="billing-actions-grid"> {/* Changed to CSS Grid */}
        {/* Billing History */}
        <div className="p-4 border rounded d-flex flex-column h-100">
          <div className="mb-3 text-success">
            <i className="fas fa-history fa-lg"></i>
          </div>
          <h6>Billing History</h6>
          <p className="text-black small flex-grow-1">View all your past transactions and invoices</p>
          <Button variant="outline-success" onClick={onViewBillingHistory}>
            <i className="fas fa-file-invoice-dollar me-2"></i> View History
          </Button>
        </div>

        {/* Pay Now / Change Plan */}
        {(showPayNowButton || showChangePlanButton) && (
          <div className="p-4 border rounded d-flex flex-column h-100">
            <div className="mb-3 text-primary">
              <i className={`fas ${showPayNowButton && !isTrialUser ? 'fa-credit-card' : 'fa-exchange-alt'} fa-lg`}></i>
            </div>
            <h6>{showPayNowButton && !isTrialUser ? 'Make a Payment' : 'Subscription Plan'}</h6>
            <p className="text-black small flex-grow-1">
              {showPayNowButton && !isTrialUser
                ? 'Make a payment for your current subscription'
                : isTrialUser
                  ? 'Upgrade from trial to a paid subscription'
                  : 'Change your current subscription plan'}
            </p>
            {showPayNowButton && !isTrialUser ? (
              <Button variant="success" onClick={onPayNow}>
                <i className="fas fa-credit-card me-2"></i> Pay Now
              </Button>
            ) : (
              showChangePlanButton && (
                <Button variant="success" onClick={onChangePlan}>
                  <i className="fas fa-exchange-alt me-2"></i> {isTrialUser ? 'Upgrade Plan' : 'Change Plan'}
                </Button>
              )
            )}
          </div>
        )}
      </div>
    </Card.Body>
  </Card>

  {/* Alerts */}
  {accountStatus.toLowerCase() === 'active' && (
    <div className="alert alert-info d-flex align-items-start">
      <i className="fas fa-info-circle me-3 mt-1"></i>
      <div>
        Your subscription is active until <strong>{nextPaymentDate}</strong>. Please renew before this date to avoid interruption.
      </div>
    </div>
  )}
  {accountStatus.toLowerCase() === 'frozen' && (
    <div className="alert alert-danger d-flex align-items-start">
      <i className="fas fa-exclamation-triangle me-3 mt-1"></i>
      <div>
        Your account is currently <strong>frozen</strong>. Please make a payment to restore full access.
      </div>
    </div>
  )}
  {accountStatus.toLowerCase() === 'cancelling' && (
    <div className="alert alert-warning d-flex align-items-start">
      <i className="fas fa-exclamation-circle me-3 mt-1"></i>
      <div>
        Your subscription will not auto-renew. You will have access until <strong>{nextPaymentDate}</strong>.
      </div>
    </div>
  )}
</div>

      )}
    </>
  );
};

export default AccountSettings;